import { formatDate, formatPrice } from '@/lib/format'
import { cn } from '@/lib/utils'
import { referralsTexts } from '../referrals.texts'
import type { BillingPeriod, ReferralCode } from '../referrals.types'
import { UsagesTable } from './UsagesTable'

interface ReferralCodeCardProps {
  code: ReferralCode
}

interface DiscountLine {
  /** Headline: first-month price (falls back to the recurring price). */
  main: string
  /** "puis X €/mois" sub-line when a months-2+ recurring price is set. */
  recurring?: string
}

const PERIOD_LABEL: Record<BillingPeriod, (price: string) => string> = {
  MONTHLY: referralsTexts.code.monthlyDiscount,
  QUARTERLY: referralsTexts.code.quarterlyDiscount,
  ANNUAL: referralsTexts.code.annualDiscount,
}

/** The code's priced cells — the per-offer grid, else the legacy Pro fields. */
function getPricedCells(code: ReferralCode) {
  if (code.prices) {
    // Name the offer only when the code spans several of them.
    const multiOffer = new Set(code.prices.map((p) => p.tier.code)).size > 1
    return code.prices.map((p) => ({
      firstPrice: p.firstPrice,
      recurringPrice: p.recurringPrice,
      label: (price: string) =>
        (multiOffer ? referralsTexts.code.offerPrefix(p.tier.name) : '') +
        PERIOD_LABEL[p.period](price),
    }))
  }
  const texts = referralsTexts.code
  return [
    {
      firstPrice: code.monthlyPrice,
      recurringPrice: code.recurringMonthlyPrice,
      label: texts.monthlyDiscount,
    },
    {
      firstPrice: code.quarterlyPrice,
      recurringPrice: code.recurringQuarterlyPrice,
      label: texts.quarterlyDiscount,
    },
    {
      firstPrice: code.annualPrice,
      recurringPrice: code.recurringAnnualPrice,
      label: texts.annualDiscount,
    },
  ]
}

function getDiscountLines(code: ReferralCode): DiscountLine[] {
  const texts = referralsTexts.code
  const lines: DiscountLine[] = getPricedCells(code)
    .filter(({ firstPrice, recurringPrice }) => firstPrice != null || recurringPrice != null)
    .map(({ firstPrice, recurringPrice, label }) => ({
      main: label(formatPrice(firstPrice ?? recurringPrice)),
      ...(recurringPrice != null && { recurring: texts.thenRecurring(formatPrice(recurringPrice)) }),
    }))
  if (lines.length === 0) {
    lines.push({ main: texts.trackingCode })
  }
  return lines
}

export const ReferralCodeCard = ({ code }: ReferralCodeCardProps) => {
  const texts = referralsTexts.code
  const discountLines = getDiscountLines(code)

  return (
    <section
      className={cn(
        'bg-background rounded-lg border border-border-light shadow-sm p-4 sm:p-6 flex flex-col gap-5',
        !code.isActive && 'opacity-60'
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-2xl sm:text-3xl font-semibold tracking-wide text-foreground break-all">
              {code.code}
            </span>
            {!code.isActive && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-background-secondary text-muted whitespace-nowrap">
                {texts.inactiveBadge}
              </span>
            )}
          </div>
          {code.description && <p className="mt-1 text-sm text-muted">{code.description}</p>}
          <div className="mt-2 flex flex-col gap-0.5">
            {discountLines.map((line) => (
              <div key={line.main}>
                <p className="text-sm font-medium text-primary">{line.main}</p>
                {line.recurring && <p className="text-xs text-muted">{line.recurring}</p>}
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-muted whitespace-nowrap">{texts.createdAt(formatDate(code.createdAt))}</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 sm:max-w-md">
        <div className="rounded-md bg-background-secondary px-4 py-3">
          <p className="text-2xl font-semibold text-foreground">{code.totalUsages}</p>
          <p className="text-xs text-muted">{texts.stats.totalUsages}</p>
        </div>
        <div className="rounded-md bg-background-secondary px-4 py-3">
          <p className="text-2xl font-semibold text-foreground">{code.activeUsages}</p>
          <p className="text-xs text-muted">{texts.stats.activeUsages}</p>
        </div>
      </div>

      <UsagesTable usages={code.usages} />
    </section>
  )
}
